import { aiSupportUrl } from 'src/_modules/AiSupportChat/adapters/craTransport';
import { parseEnvelopeCode } from 'src/_modules/AiSupportChat/core/errors';
import { ERROR_CODES } from 'src/_modules/AiSupportChat/core/protocol';
import { apiSlice } from './apiSlice';

/**
 * Keeps the app code (`error.status` is only the HTTP status). Same fields as
 * core ChatApiError, so the error can go straight to `chatReducer.apiError`:
 * - `code`: the envelope `status` (851–859, or 400 for validation); a body
 *   that isn't our envelope (fetch failure, proxy HTML) → `'network'`.
 * - `httpStatus`: null when no response arrived.
 * - `message`: the server's localized text, '' when absent.
 * Shared with src/_api/aiSupportAdminApi.js.
 */
export const transformErrorResponse = (response) => {
    const code = parseEnvelopeCode(response?.data?.status);
    const httpStatus = typeof response?.status === 'number' ? response.status : response?.originalStatus;
    const message = response?.data?.message;
    return {
        code: code ?? ERROR_CODES.NETWORK,
        httpStatus: Number.isInteger(httpStatus) ? httpStatus : null,
        message: typeof message === 'string' ? message : '',
    };
};

/**
 * Merchant / AI-developer chat endpoints. The live conversation never comes
 * from this cache: it is hydrated once into chatReducer, and `POST /chat`
 * streams over plain fetch (adapters/craTransport).
 */
export const aiSupportApi = apiSlice
    .enhanceEndpoints({ addTagTypes: ['AiEligibility', 'AiConversations', 'AiConversation'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getAiEligibility: builder.query({
                query: () => ({
                    url: aiSupportUrl('/eligibility'),
                    method: 'GET'
                }),
                providesTags: ['AiEligibility'],
                transformResponse: (response) => response?.body,
                transformErrorResponse
            }),

            // Paginated (mongoose-paginate-v2 shape); pages are accumulated by the caller.
            // `owner` (the profile id) is never sent: it only gives every user their own
            // cache entry, like the eligibility arg (D31), so a login that skipped
            // logout() never shows the previous user's titles.
            getAiConversations: builder.query({
                query: ({ page = 1, limit = 20 } = {}) => ({
                    url: aiSupportUrl('/conversations'),
                    method: 'GET',
                    params: { page, limit }
                }),
                providesTags: ['AiConversations'],
                transformResponse: (response) => response?.body,
                transformErrorResponse
            }),

            // → { conversation, messages } (messages oldest first)
            getAiConversation: builder.query({
                query: (id) => ({
                    url: aiSupportUrl(`/conversations/${encodeURIComponent(id)}`),
                    method: 'GET'
                }),
                providesTags: (result, error, id) => [{ type: 'AiConversation', id }],
                transformResponse: (response) => response?.body,
                transformErrorResponse
            }),

            sendAiFeedback: builder.mutation({
                query: ({ messageId, rating, comment }) => {
                    // A blank comment is omitted (the backend would store null for it anyway)
                    const text = typeof comment === 'string' ? comment.trim() : '';
                    return {
                        url: aiSupportUrl(`/messages/${encodeURIComponent(messageId)}/feedback`),
                        method: 'POST',
                        body: text ? { rating, comment: text } : { rating }
                    };
                },
                transformResponse: (response) => response?.body,
                transformErrorResponse
            })
        })
    });

export const {
    useGetAiEligibilityQuery,
    useGetAiConversationsQuery,
    useLazyGetAiConversationsQuery,
    useGetAiConversationQuery,
    useLazyGetAiConversationQuery,
    useSendAiFeedbackMutation,
} = aiSupportApi;
